<div class="dossier-detail-container">

  <!-- Back button -->
  <button class="btn-back" (click)="goBack()">← {{ 'dossierDetail.back' | translate }}</button>

  <!-- Loading -->
  <div class="loading" *ngIf="loading">{{ 'dossier.loading' | translate }}</div>

  <!-- Error -->
  <div class="error-message" *ngIf="error">{{ error }}</div>

  <!-- Dossier content -->
  <ng-container *ngIf="dossier && !loading">

    <!-- Header -->
    <div class="dossier-header">
      <div class="dossier-title" *ngIf="!editingExternalId">
        <h2>{{ 'dossierDetail.titlePrefix' | translate }}{{ dossier.external_id }}</h2>
        <button class="btn-icon btn-edit-ref" (click)="startEditExternalId()" title="Edit reference">✏️</button>
      </div>
      <div class="dossier-title-edit" *ngIf="editingExternalId">
        <input
          type="text"
          class="external-id-input"
          [(ngModel)]="externalIdDraft"
          maxlength="255"
          [disabled]="savingExternalId"
          (keyup.enter)="saveExternalId()"
          (keyup.escape)="cancelEditExternalId()"
          aria-label="Dossier reference"
        />
        <button class="btn-icon" (click)="saveExternalId()" [disabled]="savingExternalId" title="Save">{{ savingExternalId ? '⏳' : '✔' }}</button>
        <button class="btn-icon" (click)="cancelEditExternalId()" [disabled]="savingExternalId" title="Cancel">✖</button>
      </div>
      <span *ngIf="externalIdMsg" class="export-msg export-err">{{ externalIdMsg }}</span>
      <div class="header-meta">
        <span class="status-badge" [style.background-color]="getDossierStatusColor(dossier.status)">
          {{ dossier.status }}
        </span>
        <span class="meta-item">
          {{ 'dossierDetail.processingMode' | translate }}: {{ dossier.processing_mode }}
        </span>
        <span class="meta-item batch-indicator" *ngIf="dossier.batch_mode">
          {{ 'dossierDetail.batchMode' | translate }}
        </span>
        <label class="tf-toggle" title="Enable document examination on this dossier">
          <input type="checkbox" [checked]="isTradeFinance" (change)="toggleTradeFinance()" [disabled]="togglingTradeFinance" />
          <span>{{ 'dossierDetail.tradeFinanceToggle' | translate }}</span>
        </label>
        <button *ngIf="isTradeFinance" class="btn-examination" (click)="navigateToVerification()" [disabled]="!hasExtractedDocuments">
          {{ 'dossierDetail.documentExamination' | translate }}
        </button>
        <button
          *ngIf="isTradeFinance"
          class="btn-send-downstream"
          (click)="exportDossier()"
          [disabled]="exportingDossier || !hasTradeFinanceHeaders"
          [title]="hasTradeFinanceHeaders ? 'Send all documents to ' + (downstreamSystemName || 'destination') : 'Set customerKey, accountKey and fromCountry first'"
        >{{ exportingDossier ? '⏳' : '📤' }} Send{{ downstreamSystemName ? ' to ' + downstreamSystemName : '' }}</button>
        <span *ngIf="exportDossierMsg" class="export-msg" [class.export-ok]="exportDossierMsg.startsWith('✓')" [class.export-err]="exportDossierMsg.startsWith('✗')">{{ exportDossierMsg }}</span>
      </div>
      <div class="tradefinance-headers" *ngIf="isTradeFinance">
        <input
          type="text" class="tf-input" [(ngModel)]="tfCustomerKey" maxlength="255"
          [disabled]="savingTfHeaders" placeholder="customerKey" aria-label="customerKey"
          (keyup.enter)="saveTradeFinanceHeaders()"
        />
        <input
          type="text" class="tf-input" [(ngModel)]="tfAccountKey" maxlength="255"
          [disabled]="savingTfHeaders" placeholder="accountKey" aria-label="accountKey"
          (keyup.enter)="saveTradeFinanceHeaders()"
        />
        <input
          type="text" class="tf-input tf-input-country" [(ngModel)]="tfFromCountry" maxlength="2"
          [disabled]="savingTfHeaders" placeholder="fromCountry" aria-label="fromCountry"
          (keyup.enter)="saveTradeFinanceHeaders()"
        />
        <button class="btn-icon" (click)="saveTradeFinanceHeaders()" [disabled]="savingTfHeaders" title="Save export headers">{{ savingTfHeaders ? '⏳' : '✔' }}</button>
        <span *ngIf="tfHeadersMsg" class="export-msg export-err">{{ tfHeadersMsg }}</span>
      </div>
    </div>

    <!-- DEK unlock prompt (shown when dossier has encryption and DEK not yet unlocked) -->
    <div class="dek-unlock-section" *ngIf="needsDekUnlock && !dekUnlocked">
      <div class="dek-unlock-card">
        <div class="dek-icon">🔐</div>
        <h3>{{ 'dossierDetail.dekUnlockTitle' | translate }}</h3>
        <p class="dek-hint">{{ 'dossierDetail.dekUnlockHint' | translate }}</p>
        <div class="dek-form">
          <input
            type="password"
            [(ngModel)]="dekPassword"
            [placeholder]="'dossierDetail.dekPasswordPlaceholder' | translate"
            (keyup.enter)="unlockDek()"
            [disabled]="dekUnlocking"
            class="dek-password-input"
            aria-label="Private key password"
          />
          <button
            class="btn-primary dek-unlock-btn"
            (click)="unlockDek()"
            [disabled]="dekUnlocking || !dekPassword"
          >
            <span *ngIf="!dekUnlocking">{{ 'dossierDetail.dekUnlockButton' | translate }}</span>
            <span *ngIf="dekUnlocking">{{ 'dossierDetail.dekUnlocking' | translate }}</span>
          </button>
        </div>
        <div class="dek-error" *ngIf="dekError">
          {{ dekError }}
          <button *ngIf="noKeypairFound" class="btn-link" (click)="openMyAccountSecurity()">
            {{ 'dossierDetail.openMyAccount' | translate }}
          </button>
        </div>
      </div>
    </div>

    <!-- Upload area (only shown when DEK is unlocked or not needed) -->
    <div class="upload-section" *ngIf="dekUnlocked">
      <h3>{{ 'dossierDetail.uploadFile' | translate }}</h3>
      <div
        class="drop-zone"
        [class.drag-over]="dragOver"
        [class.uploading]="uploading"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
      >
        <div *ngIf="!uploading">
          <p>{{ 'dossierDetail.dropHint' | translate }}</p>
          <label class="btn-primary file-label">
            {{ 'dossierDetail.uploadFile' | translate }}
            <input type="file" [accept]="acceptedTypes" multiple (change)="onFileSelected($event)" hidden />
          </label>
          <p class="accepted-formats">{{ 'dossierDetail.acceptedFormats' | translate }}</p>
        </div>
        <div *ngIf="uploading" class="uploading-indicator">
          {{ 'dossierDetail.uploading' | translate }}
        </div>
      </div>
      <div class="error-message" *ngIf="uploadError">{{ uploadError }}</div>
    </div>

    <!-- Uploads list -->
    <div class="uploads-section">
      <h3>{{ 'dossierDetail.uploads' | translate }}</h3>

      <div class="empty-state" *ngIf="!dossier.uploads || dossier.uploads.length === 0">
        {{ 'dossierDetail.noUploads' | translate }}
      </div>

      <table class="uploads-table" *ngIf="dossier.uploads && dossier.uploads.length > 0">
        <thead>
          <tr>
            <th>{{ 'dossierDetail.filename' | translate }}</th>
            <th>{{ 'dossierDetail.type' | translate }}</th>
            <th>{{ 'dossierDetail.status' | translate }}</th>
            <th>{{ 'dossierDetail.created' | translate }}</th>
            <!-- Single-file uploads only — a batch/ZIP upload's per-file
                 signatures are shown in the upload review page instead. -->
            <th></th>
            <th>{{ 'dossierDetail.actionsHeader' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let u of dossier.uploads"
            (click)="navigateToUpload(u)"
            class="clickable-row"
          >
            <td>{{ u.original_filename }}</td>
            <td>{{ u.file_type }}</td>
            <td>
              <span class="status-badge" [style.background-color]="getUploadStatusColor(u.status)">
                {{ u.status }}
              </span>
            </td>
            <td>{{ u.created_at | localDateTime }}</td>
            <td class="signature-cell" (click)="$event.stopPropagation()">
              <app-signature-badge
                *ngIf="u.file_type !== 'batch'"
                [dossierId]="dossierId"
                [uploadId]="u.oid"
                (open)="openSignatureDialog(u.original_filename, u.oid, $event)"
              ></app-signature-badge>
            </td>
            <td class="actions-cell">
              <button
                *ngIf="hasOrgGroup"
                class="btn-icon btn-send-icon"
                (click)="exportUpload($event, u)"
                [disabled]="exportUploadingId === u.oid"
                [title]="'Send to ' + (downstreamSystemName || 'destination')"
              >{{ exportUploadingId === u.oid ? '⏳' : '📤' }}</button>
              <span *ngIf="exportUploadMsg[u.oid]" class="export-msg-inline" [class.ok]="exportUploadMsg[u.oid].startsWith('✓')" [class.err]="exportUploadMsg[u.oid].startsWith('✗')">{{ exportUploadMsg[u.oid] }}</span>
              <button
                *ngIf="isUploadDeletable(u)"
                class="btn-icon btn-danger-icon"
                (click)="deleteUpload($event, u)"
                [title]="'dossierDetail.deleteUpload' | translate"
              >🗑</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </ng-container>

  <app-signature-panel
    [title]="signatureDialogTitle"
    [result]="signatureDialogResult"
    [dossierId]="dossierId"
    [uploadId]="signatureDialogUploadId"
    (rechecked)="signatureDialogResult = $event"
    (close)="closeSignatureDialog()"
  ></app-signature-panel>
</div>
